import { motion } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import type { SavedLesson } from '@/mocks/lessons';

interface RelatedVideosProps {
  lesson: SavedLesson;
}

export default function RelatedVideos({ lesson }: RelatedVideosProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const t = {
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textMuted: isDark ? 'text-white/25' : 'text-black/25',
    textFaint: isDark ? 'text-white/20' : 'text-black/20',
    iconBg: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]',
    iconMuted: isDark ? 'text-white/50' : 'text-black/40',
    thumbBg: isDark ? 'bg-[#111]' : 'bg-neutral-100',
    durationBg: isDark ? 'bg-black/70 text-white' : 'bg-black/60 text-white',
    brandText: isDark ? 'text-white/20' : 'text-black/20',
    hoverBg: isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-black/[0.02]',
    thumbOverlay: isDark ? 'bg-gradient-to-t from-black/50 to-transparent' : 'bg-gradient-to-t from-black/40 to-transparent',
    playBtnBg: isDark ? 'bg-black/60' : 'bg-black/50',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
      className="px-5 pb-4"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-8 h-8 rounded-lg ${t.iconBg} flex items-center justify-center`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-movie-line ${t.iconMuted} text-sm`} />
          </div>
        </div>
        <div>
          <h3 className={`${t.textPrimary} text-sm font-semibold`}>Related Videos</h3>
          <p className={`${t.textMuted} text-[11px]`}>Continue your learning journey</p>
        </div>
      </div>

      <div className="space-y-3">
        {lesson.relatedVideos.map((video, i) => {
          const mins = Math.floor(video.duration / 60);
          const secs = Math.floor(video.duration % 60);

          return (
            <motion.button
              key={video.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.3 }}
              whileTap={{ scale: 0.97 }}
              className={`w-full text-left flex gap-3 p-2 rounded-2xl ${t.hoverBg} transition-colors cursor-pointer`}
            >
              {/* Thumbnail */}
              <div className={`relative w-[120px] h-[72px] rounded-xl overflow-hidden flex-shrink-0 ${t.thumbBg}`}>
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className={`absolute inset-0 ${t.thumbOverlay}`} />
                {/* Duration badge */}
                <div className={`absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded ${t.durationBg} text-[10px] font-semibold`}>
                  {mins}:{secs.toString().padStart(2, '0')}
                </div>
                {/* Play overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <div className={`w-8 h-8 rounded-full ${t.playBtnBg} backdrop-blur-sm flex items-center justify-center`}>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-play-fill text-white text-xs ml-0.5" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0 py-0.5">
                <h4 className={`${t.textPrimary} text-xs font-semibold leading-snug line-clamp-2 mb-1`}>{video.title}</h4>
                <p className={`${t.textMuted} text-[11px]`}>{video.subject}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className={`ri-sparkling-line ${t.brandText} text-[10px]`} />
                  </div>
                  <span className={`${t.brandText} text-[10px]`}>LAVYN Tutor</span>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}